import { getResourcePathUrl } from "owa-resource-url";
import { addProtocol } from "owa-config/lib/bootstrapOptions";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { logGreyError } from "owa-analytics";
import { addDocumentEventListener } from "owa-event-listener";

type MailboxInfo = Parameters<typeof logGreyError>[1];

const FAVICON_REL = "shortcut icon";
const FAVICON_TYPE = "image/x-icon";

export default function applyFavicon(relativeFavIconPath: string, overlayIconPath?: string): void {
    if (typeof window === "undefined") {
        // We may be in SSR? Bail either way
        return;
    }
    
    updateFaviconForWindow(relativeFavIconPath, window, "Main", overlayIconPath, true);
}

export function applyFaviconToWindow(relativeFavIconPath: string, targetWindow: Window): void {
    updateFaviconForWindow(relativeFavIconPath, targetWindow, "Main", undefined, false);
}

function updateFaviconForWindow(relativeFavIconPath: string, targetWindow: Window, tabId: string, overlayIconPath: string | undefined, notifyCallbacks: boolean): void {
    const applyFaviconInternal = () => {
        const favIconUrl = getResourcePathUrl(relativeFavIconPath);
        let favIconElement = targetWindow.document.querySelector(`link[rel="${FAVICON_REL}"]`) as HTMLLinkElement | null;
        
        if (!favIconElement) {
            favIconElement = targetWindow.document.createElement("link");
            favIconElement.rel = FAVICON_REL;
            favIconElement.type = FAVICON_TYPE;
            targetWindow.document.head.appendChild(favIconElement);
        }
        
        const fullfavIconUrl = addProtocol(favIconUrl);
        if (notifyCallbacks &&
            (favIconElement.href != fullfavIconUrl || overlayIconPath !== undefined)) {
            notifyFavIconCallbacks(tabId, fullfavIconUrl, overlayIconPath);
        }
        
        favIconElement.href = favIconUrl; //protocol automatically gets added in the href
    };
    
    if (targetWindow.document.head) {
        applyFaviconInternal();
    }
    else {
        addDocumentEventListener("applyFavicon", "DOMContentLoaded", applyFaviconInternal, targetWindow.document, { once: true });
    }
}

type FavIconCallback = (tabId: string, iconUrl: string, overlayIconUrl?: string) => void;
const favIconCallbacks: FavIconCallback[] = [];
type FavIconUpdate = {
    iconUrl: string;
    overlayIconUrl?: string;
    targetWindow?: Window;
    mailboxInfo?: MailboxInfo;
};
const pendingFavIconUpdates = new Map<string, FavIconUpdate>();

export function registerFavIconCallback(cb: FavIconCallback): void {
    favIconCallbacks.push(cb);
    for (const [tabId, update] of pendingFavIconUpdates) {
        if (!update.targetWindow?.closed) {
            try {
                cb(tabId, update.iconUrl, update.overlayIconUrl);
            }
            catch (error) {
                logUpdateWindowIconError(error, update.mailboxInfo);
            }
        }
    }
    pendingFavIconUpdates.clear();
}

function notifyFavIconCallbacks(tabId: string, iconUrl: string, overlayIconUrl?: string): void {
    if (favIconCallbacks.length === 0) {
        pendingFavIconUpdates.set(tabId, { iconUrl, overlayIconUrl });
        return;
    }
    
    for (const cb of favIconCallbacks) {
        cb(tabId, iconUrl, overlayIconUrl);
    }
}

/**
 * Update both the browser favicon and native-host window icon for a projection popout.
 */
export function applyProjectionIcon(tabId: string, projectionWindow: Window, iconUrl: string, mailboxInfo: MailboxInfo, overlayIconUrl?: string): void {
    updateFaviconForWindow(iconUrl, projectionWindow, tabId, overlayIconUrl, false);
    
    const fullIconUrl = addProtocol(getResourcePathUrl(iconUrl));
    if (favIconCallbacks.length === 0) {
        pendingFavIconUpdates.set(tabId, {
            iconUrl: fullIconUrl,
            overlayIconUrl,
            targetWindow: projectionWindow,
            mailboxInfo,
        });
        projectionWindow.addEventListener("pagehide", () => {
            if (pendingFavIconUpdates.get(tabId)?.targetWindow === projectionWindow) {
                pendingFavIconUpdates.delete(tabId);
            }
        }, { once: true });
        return;
    }
    
    for (const cb of favIconCallbacks) {
        try {
            cb(tabId, fullIconUrl, overlayIconUrl);
        }
        catch (error) {
            logUpdateWindowIconError(error, mailboxInfo);
        }
    }
}

function logUpdateWindowIconError(error: unknown, mailboxInfo: MailboxInfo | undefined): void {
    const errorToLog = error instanceof Error
        ? error
        : Object.assign(new Error("Apply projection icon callback failed"), { cause: error });
    
    logGreyError("UpdateWindowIconError", mailboxInfo ?? getGlobalSettingsAccountMailboxInfo(), errorToLog);
}
